import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, ChevronDown, User as UserIcon, Home, Key, MapPin, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import domusLogo from "@/assets/logo-icon-circle.png";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Navigation = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<'owner' | 'tenant' | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadUserRole(session.user.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserRole(session.user.id);
      } else {
        setUserRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserRole = async (userId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      if (error) {
        console.error('Error loading user role:', error);
        return;
      }
      const roles: string[] = (data || []).map((r: any) => r.role);
      setUserRole((["admin", "owner", "tenant"].find((r) => roles.includes(r)) as any) || null);

    } catch (error) {
      console.error('Error loading user role:', error);
    }
  };

  // --- Data ---
  const serviziDropdownItems = [
    { path: '/affitti-milano', label: 'Affitti Milano' },
    { path: '/affitti-napoli', label: 'Affitti Napoli' },
    { path: '/tariffe-servizi', label: t('nav.rates') },
    { path: '/chi-perche', label: t('nav.whoWhy') },
    { path: '/servizi-premium', label: t('nav.premiumServices') },
  ];

  const mobileMenuItems = [
    { path: '/iscriviti-ora', label: 'Pubblica Annuncio', isPrimary: true },
    { path: '/canone-concordato', label: 'Canone Concordato' },
    { path: '/cerca-annunci', label: t('nav.searchListings') },
    { path: '/community-studenti', label: 'Student Community', isSecondary: true },
  ];

  // --- Shared user menu ---
  const renderUserMenuItems = (onClose: () => void) => (
    <>
      <Link
        to="/dashboard/profilo"
        onClick={onClose}
        className="py-2 px-3 text-sm flex items-center text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors"
      >
        <UserIcon className="mr-2 h-4 w-4" />
        {t('nav.myProfile')}
      </Link>
      {userRole === 'owner' && (
        <Link
          to="/dashboard"
          onClick={onClose}
          className="py-2 px-3 text-sm flex items-center text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors"
        >
          <Home className="mr-2 h-4 w-4" />
          {t('nav.myListings')}
        </Link>
      )}
      {userRole === 'tenant' && (
        <Link
          to="/dashboard"
          onClick={onClose}
          className="py-2 px-3 text-sm flex items-center text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors"
        >
          <Key className="mr-2 h-4 w-4" />
          {t('nav.myRequests')}
        </Link>
      )}
    </>
  );

  // --- Mobile Sheet Content ---
  const renderMobileSheetContent = () => (
    <SheetContent side="right" className="w-[280px] sm:w-[350px]">
      <nav className="flex flex-col gap-4 mt-8">
        {mobileMenuItems.map(({ path, label, isPrimary, isSecondary }) => (
          <Link key={path} to={path} onClick={() => setIsOpen(false)} className="w-full">
            <Button
              variant={isPrimary ? "default" : isSecondary ? "outline" : "ghost"}
              className={`justify-start h-12 w-full ${!isPrimary && !isSecondary ? 'text-foreground hover:text-primary' : ''}`}
            >
              <span className="text-base">{label}</span>
            </Button>
          </Link>
        ))}

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="servizi" className="border-none">
            <AccordionTrigger className="py-3 px-4 hover:no-underline hover:bg-accent rounded-md">
              <span className="text-base text-foreground">Servizi</span>
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <div className="flex flex-col gap-1 pl-4 pt-2">
                {serviziDropdownItems.map(({ path, label }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setIsOpen(false)}
                    className="py-2 px-3 text-sm text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {user ? (
          <Accordion type="single" collapsible className="w-full mt-4">
            <AccordionItem value="user-menu" className="border-none">
              <AccordionTrigger className="py-3 px-4 hover:no-underline bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
                <div className="flex items-center">
                  <UserIcon className="mr-2 h-4 w-4" />
                  <span className="text-base">{t('nav.myAccount')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0">
                <div className="flex flex-col gap-1 pl-4 pt-2">
                  {renderUserMenuItems(() => setIsOpen(false))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : (
          <Link to="/auth" onClick={() => setIsOpen(false)} className="w-full mt-4">
            <Button variant="outline" className="w-full h-12">
              {t('nav.login')}
            </Button>
          </Link>
        )}
      </nav>
    </SheetContent>
  );

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity shrink-0">
            <img
              src={domusLogo}
              alt="Logo Domus Flexi – poltrona e lampada stilizzata"
              className="h-10 w-10 object-contain"
              width={40}
              height={40}
            />
          </Link>

          {/* ===== Desktop Navigation (lg+) ===== */}
          <div className="hidden lg:flex items-center gap-2">
            {/* CTA Primaria – Proprietari */}
            <Link to="/iscriviti-ora">
              <Button variant="default">Pubblica Annuncio</Button>
            </Link>

            {/* Link – Cerca Annunci */}
            <Link to="/cerca-annunci">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                {t('nav.searchListings')}
              </Button>
            </Link>

            {/* Link – Canone Concordato */}
            <Link to="/canone-concordato">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                Canone Concordato
              </Button>
            </Link>

            {/* CTA Secondaria – Studenti */}
            <Link to="/community-studenti">
              <Button variant="outline">Student Community</Button>
            </Link>

            {/* Servizi Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-foreground hover:text-primary">
                  Servizi
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover z-[100]">
                {serviziDropdownItems.map(({ path, label }) => (
                  <DropdownMenuItem key={path} asChild>
                    <Link to={path} className="cursor-pointer hover:bg-primary/10">
                      {label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Account */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="default" className="ml-2">
                    <UserIcon className="mr-2 h-4 w-4" />
                    {t('nav.myAccount')}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover z-[100]">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/profilo" className="cursor-pointer hover:bg-primary/10">
                      <UserIcon className="mr-2 h-4 w-4" />
                      {t('nav.myProfile')}
                    </Link>
                  </DropdownMenuItem>
                  {userRole === 'owner' && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer hover:bg-primary/10">
                        <Home className="mr-2 h-4 w-4" />
                        {t('nav.myListings')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {userRole === 'tenant' && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer hover:bg-primary/10">
                        <Key className="mr-2 h-4 w-4" />
                        {t('nav.myRequests')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button variant="outline" className="ml-2">
                  {t('nav.login')}
                </Button>
              </Link>
            )}

            <LanguageSwitcher />
          </div>

          {/* ===== Tablet Navigation (md only) ===== */}
          <div className="hidden md:flex lg:hidden items-center gap-1">
            <Link to="/iscriviti-ora">
              <Button variant="default" size="sm">Pubblica Annuncio</Button>
            </Link>
            <Link to="/community-studenti">
              <Button variant="outline" size="sm">Student Community</Button>
            </Link>
            <Link to="/cerca-annunci">
              <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
                {t('nav.searchListings')}
              </Button>
            </Link>
            <LanguageSwitcher />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menu" className="text-foreground">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              {renderMobileSheetContent()}
            </Sheet>
          </div>

          {/* ===== Mobile Navigation (< md) ===== */}
          <div className="flex items-center gap-2 md:hidden">
            <Link to="/iscriviti-ora">
              <Button variant="default" size="sm">Pubblica Annuncio</Button>
            </Link>
            <LanguageSwitcher />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menu" className="text-foreground">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              {renderMobileSheetContent()}
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
